/**
 * When we may ask for notification permission, and when we must not.
 *
 * Pure on purpose: the browser rules here are fiddly enough to be worth testing
 * without a browser, and the iOS case in particular is easy to get wrong in a
 * way that only shows up on somebody's phone.
 */

/** What the browser told us about itself. */
export interface NotificationEnvironment {
  /** `'Notification' in window`. On iOS this is false in a Safari tab. */
  hasNotificationApi: boolean;
  hasServiceWorker: boolean;
  /** `'PushManager' in window`. Needed for push; not for local notifications. */
  hasPushManager: boolean;
  isIos: boolean;
  /** Running from the Home Screen rather than a browser tab. */
  isStandalone: boolean;
  /** `Notification.permission`, or null when the API is missing. */
  permission: NotificationPermission | null;
}

export type NotificationStatus =
  /** This browser cannot do it at all. Nothing to offer. */
  | 'unsupported'
  /** iOS can, but only once the app is on the Home Screen. */
  | 'needs-install'
  /** We can ask. */
  | 'default'
  | 'granted'
  /** They said no. Only the OS settings can undo this, not us. */
  | 'denied';

/**
 * What this device can actually do right now.
 *
 * The iOS rule is the important one: Safari has supported web push since 16.4,
 * but **only for apps installed to the Home Screen**. In a tab the Notification
 * API is simply absent, so "unsupported" would be a lie - the honest answer is
 * "install it first", which is a thing the person can act on.
 */
export function notificationStatus(env: NotificationEnvironment): NotificationStatus {
  if (!env.hasNotificationApi || !env.hasServiceWorker || !env.hasPushManager) {
    return env.isIos && !env.isStandalone ? 'needs-install' : 'unsupported';
  }
  if (env.permission === 'granted') return 'granted';
  if (env.permission === 'denied') return 'denied';
  return 'default';
}

/** How long "Not now" holds the prompt off. */
export const SNOOZE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether to put the prompt on screen.
 *
 * Only ever for the two states a person can act on - we can ask, or they need
 * to install first. Never when already granted, never when denied (only the OS
 * settings can undo that, so asking again is just nagging), and never inside the
 * snooze window after a dismissal.
 */
export function shouldShowPrompt(
  status: NotificationStatus,
  dismissedAtMs: number | null,
  nowMs: number,
): boolean {
  if (status !== 'default' && status !== 'needs-install') return false;
  if (dismissedAtMs === null) return true;
  return nowMs - dismissedAtMs >= SNOOZE_DAYS * DAY_MS;
}
